import _ from 'lodash';
import hash from 'object-hash';
import { EditStore, EditStoreBase, EditStoreProps } from '@/main/store/@types/edit.d';
import { Status } from '@/main/store/@types/page';
import { objectToCamelCaseShallow } from '@/main/utils/stringHelper';
import { cellNames, columnsToCamelCase, Row, Table } from '@/main/utils/table';
import { STORE_TABLE, UPDATE_BASIC_TABLE_DATA } from '@/main/store/listEditTypes';
import { TableDataMutationInput } from '@/main/store/@types/store.d';

import { OZAKAZKA_ON_VOZIDLO } from '../../constants';
import { types } from '../types';
import { Edit, LocalState, ZakazkaDropdownList } from './state';

type State = EditStore<Edit, ZakazkaDropdownList, LocalState, {}, EditStoreProps>;

type Data<T = {}> = { data: any & T };

export const mutations = {
	[types.mutations.SHOW_ITN_CARD](state: State, { data }: Data) {
		state.localState.showIntCard = data;
	},
	[types.mutations.ZAKAZKA_PRACOVNIK_SELECT_MAPPER](state: State, { data }: Data) {
		state.edit.PracovnikId = data.value;
		state.edit.PracovnikIdText = data.jmeno || data.text ? `${data.text} ${data.jmeno}` : null;
		state.edit.PracovnikOsobniCis = data.osobniCis;
	},
	[types.mutations.ZAKAZKA_LAK_DRUH_SELECT_MAPPER](state: State, { data }: Data) {
		state.edit.LakdruhId = data.id;
		state.edit.LakdruhOzn = data.value;
		state.edit.LakdruhOznText = data.text;
	},
	[types.mutations.ZAKAZKA_ZAKAZNIK_SELECT_MAPPER](state: State, { data }: Data) {
		if (state.edit.ZakaznikId !== data.value) {
			state.edit.ZakaznikId = data.value;
			state.edit.ZakaznikIdText = data.text;
		}
	},
	[types.mutations.ZAKAZKA_SMLUVNI_PARTNER_SELECT_MAPPER](state: State, { data }: Data) {
		if (state.edit.SmluvpartnerId !== data.value) {
			state.edit.SmluvpartnerId = data.value;
			state.edit.SmluvpartnerIdText = data.text;
		}
	},
	[types.mutations.ZAKAZKA_POJISTOVNA_SELECT_MAPPER](state: State, { data }: Data) {
		if (state.edit.PojistovnaId !== data.value) {
			state.edit.PojistovnaId = data.value;
			state.edit.PojistovnaIdText = data.text;
			state.localState.SplitOrderValidationPopup = false;
		}
		if (state.edit.PojistovnaId === null && state.edit.IsSplittedOrder!) {
			state.localState.SplitOrderValidationPopup = true;
		}
	},
	[types.mutations.ZAKAZNIK_CDB](state: State, { data }: Data) {
		const changed = state.editHash !== hash(state.edit);
		if (state.edit.ZakaznikCDB !== data) {
			state.edit.ZakaznikCDB = data;
		}
		if (!changed) {
			state.editHash = hash(state.edit);
		}
	},
	[types.mutations.ZAKAZKA_LEASING_SELECT_MAPPER](state: State, { data }: Data) {
		if (state.edit.LeaszakaznikId !== data.value) {
			state.edit.LeaszakaznikId = data.value;
			state.edit.LeaszakaznikIdText = data.text;
		}
	},
	[types.mutations.ZAKAZKA_JINY_PRIJEMCE_SELECT_MAPPER](state: State, { data }: Data) {
		if (state.edit.JinyprijemceId !== data.value) {
			state.edit.JinyprijemceId = data.value;
			state.edit.JinyprijemceIdText = data.text;
		}
	},
	[types.mutations.ZAKAZKA_REF_SELECT_MAPPER](state: State, { data }: Data) {
		state.edit.ZakazkaIdRef = data.value;
		state.edit.ZakazkaIdRefText = data.text;
	},
	[types.mutations.ZAKAZKA_VOZIDLO_SELECT_MAPPER](state: State, { data }: Data) {
		if (state.edit.VozidloId !== data.value) {
			state.edit.VozidloId = data.value;
			state.edit.VozidloText = data.text;
			state.edit.VozidloVin = data.karoserieNr;
			state.edit.VozidloRzv = data.spz;
			state.edit.IsNonGenuine = data.IsNonGenuine;
			state.edit.NonGenuineRemark = data.NonGenuineRemark;
			state.edit.IsTotalLoss = data.IsTotalLoss;
			state.edit.TotalLossRemark = data.TotalLossRemark;
		}
	},
	[types.mutations.ZAKAZKA_INTER_DRUH_SELECT_MAPPER](state: State, { data }: Data) {
		if (state.edit.IntrDruhOzn !== data.IntrDruhOzn) {
			state.edit.InterDruhNs = data.InterDruhNs;
			state.edit.IntrDruhOzn = data.IntrDruhOzn;
			state.edit.IntrDruhOznText = data.IntrDruhOznText;
		}
	},
	[types.mutations.ZAKAZKA_PNV_SELECT_MAPPER](state: State, { data }: Data) {
		state.edit.PnvId = data.value;
		state.localState.PnvNr = data.text;
	},
	[types.mutations.ZAKAZKA_POV_SELECT_MAPPER](state: State, { data }: Data) {
		state.edit.PovId = data.povId;
		state.edit.PovNr = data.povNr;
	},
	[types.mutations.ZAKAZKA_NV_SELECT_MAPPER](state: State, { data }: Data) {
		state.localState.NvskladIdText = data.text;
		state.edit.NvskladId = data.value;
		state.edit.PnvId = data.pnvId;
		state.localState.PnvNr = data.pnvNr;
	},
	[types.mutations.ZAKAZKA_OV_SELECT_MAPPER](state: State, { data }: Data) {
		state.edit.OvozidloId = data.value;
		state.edit.OvozidloIdText = data.text;
	},
	[types.mutations.LOAD_MDV_VOZIDLO](state: State, { data }: Data) {
		state.edit.NvskladId = data.NvskladId;
		state.localState.NvskladIdText = `${data.NvprijemkaPfx} ${data.NvprijemkaNr} ${data.KaroserieNr}`;
		state.localState.NvozidloVin = data.KaroserieNr;
		state.localState.PnvKomiseNr = data.KomiseNr;
	},
	[types.mutations.LOAD_MDV_PNV](state: State, { data }: Data) {
		state.edit.PnvId = data.PnvId;
		state.localState.PnvNr = data.PnvNr;
	},
	[types.mutations.ZAKAZKA_ADD_FAKT_TEXT](state: State, { data }: Data) {
		state.edit.TextRtf = _.join([state.edit.TextRtf, data.text].filter(Boolean), ' ');
	},
	[types.mutations.ZAKAZKA_RESET_SELECTED_TAB](state: State) {
		state.localState.selectedTab = null;
	},
	[types.mutations.ZAKAZKA_KALKULACE_STREDNAKL_SELECT](state: State, { data }: Data) {
		state.edit.KalkStredNaklOzn = data.value;
		state.edit.KalkStredNaklOznText = data.text;
	},
	[types.mutations.ZAKAZKA_KALKULACE_PRACOVNIK_SELECT](state: State, { data }: Data) {
		state.edit.KalkPracovnikId = data.value;
		state.edit.KalkPracovnikIdText = data.jmeno || data.text ? `${data.text} ${data.jmeno}` : null;
		state.edit.KalkPracovnikOsobniCislo = data.osobniCis;
	},
	[types.mutations.SET_KALKULACE_TABLE_SCHEMA_AND_ROWS](state: State, { data }: Data) {
		const table = new Table();
		table.columns = columnsToCamelCase(data.Columns);
		table.cellNames = cellNames(table);
		table.operations = data.Operations;
		table.hasIdColumn = data.HasIdColumn;
		table.status = Status.ok;
		table.rows = data.Rows.map((row: any) => objectToCamelCaseShallow(row));
		state.localState.kalkulaceTable = table;
	},
	[types.mutations.CHECK_IN_SELECT](state: State, { data }: Data) {
		state.edit.CheckinId = data.id;
		state.edit.CheckinNr = data.value;
		state.edit.CheckinVstupDat = data.vstupDat;
	},
	[types.mutations.ZAKAZNIK_CUSTOMER_FIRST_VISIT](state: State, { data }: Data) {
		if (data.IsCustomerFirstVisit) {
			state.edit.FirstVisitVisible = true;
			state.edit.IsCustomerFirstVisit = true;
		} else {
			state.edit.FirstVisitVisible = false;
		}
	},

	[types.mutations.SERVICE_PACKAGE](state: State, { data }: Data) {
		state.edit.ServicePackageId = data.value;
		state.edit.ServicePackageTxt = data.text;
		state.edit.CategoryName = data.CategoryName;
		state.edit.ServiceOrderType = data.ServiceOrderType;
		state.edit.PackageType = data.PackageType;
	},

	[types.mutations.SET_CAR_DESCRIPTION](state: State, { data }: Data<string | null>) {
		const { entryData, equipmentData } = state.localState.carDescription;

		if (data.EntryData) {
			entryData.columns = columnsToCamelCase(data.EntryData.Columns);
			entryData.cellNames = cellNames(state.localState.carDescription.entryData);
			entryData.operations = data.EntryData.Operations;
			entryData.hasIdColumn = data.EntryData.HasIdColumn;
			entryData.status = Status.ok;
			entryData.rows = data.EntryData.Rows.map((row: any) => objectToCamelCaseShallow(row));
		}

		if (data.EquipmentData) {
			equipmentData.columns = columnsToCamelCase(data.EquipmentData.Columns);
			equipmentData.cellNames = cellNames(state.localState.carDescription.equipmentData);
			equipmentData.operations = data.EquipmentData.Operations;
			equipmentData.hasIdColumn = data.EquipmentData.HasIdColumn;
			equipmentData.status = Status.ok;
			equipmentData.rows = data.EquipmentData.Rows.map((row: any) => objectToCamelCaseShallow(row));
			equipmentData.addRowId();

			state.localState.carDescription.enabled = true;
		}
	},
	[types.mutations.SET_TISK_DIALOG](state: State, { visible, url, filter, fetchPdfProvider }:
	{ visible: boolean, url?: string, filter?: Record<string, any>, fetchPdfProvider?: ((filter: {}) => Promise<any>) }) {
		state.localState.tiskDialog.url = url ?? null;
		state.localState.tiskDialog.filter = filter ?? null;
		state.localState.tiskDialog.fetchPdfProvider = fetchPdfProvider ?? null;
		state.localState.tiskDialog.visible = visible;
	},
	[UPDATE_BASIC_TABLE_DATA](state: EditStoreBase, { data, tableName = STORE_TABLE, isEditable = false }: TableDataMutationInput) {
		const table = _.get(state.dependentTables, tableName) as Table;
		table.status = Status.ok;
		if (tableName === OZAKAZKA_ON_VOZIDLO) {
			const transformedPlanovanaPrp = data.map((e: any) => ({ cells: _.fromPairs(_.toPairs(e).map(([, val], idx) => [idx, val])) }));
			_.isArray(data) && (table.rows = transformedPlanovanaPrp);
		} else {
			_.isArray(data) && (table.rows = data.map((row: Row) => objectToCamelCaseShallow(row)));
		}
		(table.isEditable || isEditable) && (table.hash = hash(table.rows));
	},
	[types.mutations.GET_CUSTOMER_CAMPAIGN_DETAILS](state: State, { data }: Data<string | null>) {
		if (data && data.length && !state.localState.viewDialogCampaign) {
			state.localState.CampaignData = data;
			state.localState.viewDialogCampaign = true;
		}
	},
	
};
